import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import useSubmitGuard from '../hooks/useSubmitGuard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  ShoppingCart, 
  Plus,
  Clock, 
  CheckCircle, 
  Truck,
  Package,
  AlertTriangle,
  RefreshCw,
  Upload,
  FileText,
  Trash2,
  Download,
  Edit2,
  Quote,
  Building2,
  History,
  Search
} from 'lucide-react';
import { format } from 'date-fns';
import { ExportButton } from '../utils/exportUtils';
import { offlineDB } from '../utils/offlineDB';
import DeleteRequestDialog from '../components/DeleteRequestDialog';
import PendingDeletionsPanel from '../components/PendingDeletionsPanel';
import { generatePOPdf } from '../utils/poGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'Pending Approval', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  ordered: { label: 'Ordered', color: 'bg-indigo-100 text-indigo-800', icon: ShoppingCart },
  partial: { label: 'Partial Delivery', color: 'bg-orange-100 text-orange-800', icon: Truck },
  received: { label: 'Fully Received', color: 'bg-green-100 text-green-800', icon: Package },
  issued: { label: 'Issued', color: 'bg-emerald-100 text-emerald-800', icon: Truck },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
};

const URGENCY_CONFIG = {
  normal: { label: 'Normal', color: 'bg-slate-100 text-slate-700' },
  urgent: { label: 'Urgent', color: 'bg-orange-100 text-orange-700' },
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700' },
};

const VIEW_CONFIG = {
  indent:     { label: 'Indent',               emptyMsg: 'No new indents raised', statuses: ['pending'] },
  quotations: { label: 'Quotations Received',  emptyMsg: 'No approved indents awaiting quotation comparison', statuses: ['approved'] },
  pending:    { label: 'Pending Purchase Orders', emptyMsg: 'No pending orders — all deliveries are up to date', statuses: ['ordered', 'partial'] },
  completed:  { label: 'Completed This Month', emptyMsg: 'No completed orders this month', statuses: ['received', 'issued', 'cancelled'] },
  history:    { label: 'Purchase History',     emptyMsg: 'No purchase history yet', statuses: ['received', 'issued', 'cancelled'] },
};

const classifyPR = (pr) => {
  if (pr.status === 'pending') return 'indent';
  if (pr.status === 'approved') return 'quotations';
  if (['ordered', 'partial'].includes(pr.status)) return 'pending';
  return 'completed'; // received, issued, cancelled
};

const PurchaseOrdersPage = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const activeView = location.pathname.endsWith('/quotations') || location.pathname.endsWith('/quotations/received') ? 'quotations'
    : location.pathname.endsWith('/pending') ? 'pending'
    : location.pathname.endsWith('/completed') ? 'completed'
    : location.pathname.endsWith('/history') ? 'history'
    : 'indent';
  const canApprove = user?.is_admin || user?.is_super_admin;
  const guard = useSubmitGuard();

  // Delete approval
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pendingDelRequests, setPendingDelRequests] = useState([]);

  const fetchPendingDeletions = async () => {
    if (!canApprove) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/delete-requests?status=pending&module=purchase_orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingDelRequests(res.data);
    } catch {}
  };

  const handleApproveDelete = async (reqId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deletion approved');
      fetchData();
      fetchPendingDeletions();
    } catch { toast.error('Failed to approve'); }
  };

  const handleRejectDelete = async (reqId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/reject`, { note: 'Rejected' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Request rejected');
      fetchPendingDeletions();
    } catch { toast.error('Failed to reject'); }
  };

  const handleDeletePR = async (pr) => {
    if (canApprove) {
      if (!window.confirm(`Delete PO #${pr.po_number || pr.id.slice(0, 8)} permanently?`)) return;
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/purchase-requisitions/${pr.id}`, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Purchase order deleted');
        fetchData();
        fetchPendingDeletions();
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
    } else {
      setDeleteTarget({ id: pr.id, display: `PO: ${pr.part_name} (${pr.po_number || pr.id.slice(0, 8)})` });
    }
  };

  const [requisitions, setRequisitions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [settings, setSettings] = useState({ site_addresses: [] });
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [grnDialogOpen, setGrnDialogOpen] = useState(false);
  const [grnHistoryOpen, setGrnHistoryOpen] = useState(false);
  const [grnHistory, setGrnHistory] = useState([]);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueToName, setIssueToName] = useState('');
  const [issuePrId, setIssuePrId] = useState(null);
  const [selectedPR, setSelectedPR] = useState(null);

  // Edit PO
  const [editPoOpen, setEditPoOpen] = useState(false);
  const [editPoForm, setEditPoForm] = useState({});

  // Detail / Ticket View
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailPR, setDetailPR] = useState(null);

  // Vendor Quotes
  const [quotesOpen, setQuotesOpen] = useState(false);
  const [quotesData, setQuotesData] = useState([]);
  const [applyToAll, setApplyToAll] = useState(false);
  const [newQuote, setNewQuote] = useState({ vendor_name: '', vendor_contact: '', vendor_email: '', unit_price: '', gst_percentage: 18, quantity: 1, quote_date: new Date().toISOString().split('T')[0], notes: '' });
  
  const [newPR, setNewPR] = useState({
    part_name: '',
    part_number: '',
    quantity_requested: 1,
    estimated_unit_price: '',
    vendor_name: '',
    urgency: 'normal',
    notes: '',
    source: 'manual',
    purchase_type: 'ho',
    consignee_address: ''
  });
  
  const [orderForm, setOrderForm] = useState({
    actual_unit_price: '',
    vendor_name: '',
    quantity_ordered: ''
  });
  
  const [grnForm, setGrnForm] = useState({
    quantity_received: '',
    invoice_ref: '',
    delivery_note: '',
    received_date: new Date().toISOString().split('T')[0],
    notes: '',
    part_number_confirmed: '',
  });

  useEffect(() => {
    fetchData();
    fetchSettings();
    fetchPendingDeletions();
  }, []);

  // Auto-open create dialog when navigated from sidebar "Create Purchase Order"
  useEffect(() => {
    if (location.search.includes('create=1')) {
      setCreateDialogOpen(true);
      navigate('/inventory/purchase-orders/indent', { replace: true });
    }
  }, [location.search]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(res.data || { site_addresses: [] });
    } catch {}
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [prRes, statsRes] = await Promise.all([
        axios.get(`${API}/purchase-requisitions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/purchase-requisitions/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setRequisitions(prRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePR = async (e) => {
    e.preventDefault();
    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/purchase-requisitions`, newPR, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Purchase Requisition created');
        setCreateDialogOpen(false);
        setNewPR({ part_name: '', part_number: '', quantity_requested: 1, estimated_unit_price: '', vendor_name: '', urgency: 'normal', notes: '', source: 'manual', purchase_type: 'ho', consignee_address: '' });
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to create PR');
      }
    });
  };

  const filteredRequisitions = useMemo(() => {
    let list;
    const terminal = requisitions.filter(pr => classifyPR(pr) === 'completed');
    if (activeView === 'history') {
      list = terminal;
    } else if (activeView === 'completed') {
      const now = new Date();
      const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      list = terminal.filter(pr => (pr.updated_at || pr.created_at || '').startsWith(monthStr));
    } else {
      list = requisitions.filter(pr => classifyPR(pr) === activeView);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(pr =>
        (pr.part_name || '').toLowerCase().includes(q) ||
        (pr.part_number || '').toLowerCase().includes(q) ||
        (pr.pr_number || '').toLowerCase().includes(q) ||
        (pr.po_number || '').toLowerCase().includes(q) ||
        (pr.door_no || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [requisitions, activeView, searchTerm]);

  const handleEditPO = async () => {
    if (!selectedPR) return;
    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API}/purchase-requisitions/${selectedPR.id}/edit`, editPoForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('PO updated');
        setEditPoOpen(false);
        setSelectedPR(null);
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to update PO');
      }
    });
  };

  const openEditPO = (pr) => {
    setSelectedPR(pr);
    setEditPoForm({
      part_name: pr.part_name,
      part_number: pr.part_number || '',
      quantity_requested: pr.quantity_requested,
      vendor_name: pr.vendor_name || '',
      actual_unit_price: pr.actual_unit_price || pr.estimated_unit_price || 0,
      notes: pr.notes || '',
      consignee_address: pr.consignee_address || '',
      purchase_type: pr.purchase_type || 'ho',
      urgency: pr.urgency || 'normal',
      gst_percentage: pr.gst_percentage || 18,
      discount: pr.discount || 0,
      payment_terms: pr.payment_terms || '',
      delivery: pr.delivery || '',
    });
    setEditPoOpen(true);
  };

  const handleDownloadPO = (pr) => {
    generatePOPdf({
      po_number: pr.po_number || `LSN/HO/${new Date().getFullYear().toString().slice(-2)}-${(new Date().getFullYear() + 1).toString().slice(-2)}/${pr.pr_number}`,
      order_date: pr.ordered_at ? format(new Date(pr.ordered_at), 'd/M/yyyy') : format(new Date(), 'd/M/yyyy'),
      vendor_name: pr.vendor_name || '',
      vendor_contact: pr.vendor_contact || '',
      vendor_email: pr.vendor_email || '',
      subject: pr.part_name,
      door_no: pr.door_no || '',
      indent_no: pr.pr_number,
      ref_no: 'Your quote',
      consignee_address: pr.consignee_address || '',
      payment_terms: pr.payment_terms || '15 Days Credit',
      delivery: pr.delivery || 'At site',
      discount: pr.discount || 0,
      gst_percentage: pr.gst_percentage || 18,
      line_items: [{
        part_no: pr.part_number || '',
        description: pr.part_name,
        uom: 'NOS',
        req_qty: pr.quantity_ordered || pr.quantity_requested,
        tax_pct: pr.gst_percentage || 18,
        unit_price: pr.actual_unit_price || pr.estimated_unit_price || 0,
        total_price: (pr.actual_unit_price || pr.estimated_unit_price || 0) * (pr.quantity_ordered || pr.quantity_requested)
      }]
    });
  };

  const openVendorQuotes = async (pr) => {
    setSelectedPR(pr);
    setNewQuote(q => ({ ...q, part_name: pr.part_name, part_number: pr.part_number || '', quantity: pr.quantity_requested }));
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/vendor-quotes?pr_id=${pr.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setQuotesData(res.data);
    } catch { setQuotesData([]); }
    setQuotesOpen(true);
  };

  const handleAddQuote = async () => {
    if (!newQuote.vendor_name || !newQuote.unit_price) { toast.error('Vendor name and price required'); return; }
    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // Save quote for current PR
        await axios.post(`${API}/vendor-quotes`, { ...newQuote, pr_id: selectedPR?.id || '' }, { headers });

        // If "apply to all matching indents" is checked, save to all PRs with same part
        if (applyToAll && selectedPR) {
          const matchKey = (selectedPR.part_name || '').toLowerCase();
          const matchingPRs = requisitions.filter(pr =>
            pr.id !== selectedPR.id &&
            (pr.part_name || '').toLowerCase() === matchKey
          );
          for (const pr of matchingPRs) {
            try {
              await axios.post(`${API}/vendor-quotes`, { ...newQuote, pr_id: pr.id }, { headers });
            } catch { /* skip failures for individual PRs */ }
          }
          toast.success(`Quote saved to ${matchingPRs.length + 1} indents with "${selectedPR.part_name}"`);
        } else {
          toast.success('Quote saved');
        }

        const res = await axios.get(`${API}/vendor-quotes?pr_id=${selectedPR?.id}`, { headers });
        setQuotesData(res.data);
        setNewQuote(q => ({ ...q, vendor_name: '', vendor_contact: '', vendor_email: '', unit_price: '', notes: '' }));
      } catch { toast.error('Failed to save quote'); }
    });
  };

  const handleDeleteQuote = async (quoteId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/vendor-quotes/${quoteId}`, { headers: { Authorization: `Bearer ${token}` } });
      setQuotesData(d => d.filter(q => q.id !== quoteId));
      toast.success('Quote removed');
    } catch { toast.error('Failed to remove quote'); }
  };

  const handleApprove = async (prId) => {
    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API}/purchase-requisitions/${prId}/approve`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Approved');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to approve');
      }
    });
  };

  const handleMarkOrdered = async () => {
    if (!selectedPR) return;
    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({
          actual_unit_price: orderForm.actual_unit_price.toString(),
          vendor_name: orderForm.vendor_name,
          quantity_ordered: (orderForm.quantity_ordered || selectedPR.quantity_requested).toString()
        });
        
        const response = await axios.put(
          `${API}/purchase-requisitions/${selectedPR.id}/order?${params.toString()}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.price_alert) {
          toast.warning(`Order recorded. Price Alert: ${response.data.alert_message}`);
        } else {
          toast.success(`Order recorded. PO: ${response.data.po_number || 'auto-generated'}`);
        }
        
        setOrderDialogOpen(false);
        setSelectedPR(null);
        setOrderForm({ actual_unit_price: '', vendor_name: '', quantity_ordered: '' });
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to record order');
      }
    });
  };

  const handleAddGRN = async () => {
    if (!selectedPR) return;

    if (!navigator.onLine) {
      try {
        await offlineDB.add({
          module: 'grn',
          displayName: `GRN – ${selectedPR.part_name || 'Unknown'}`,
          endpoint: `/purchase-requisitions/${selectedPR.id}/grn`,
          method: 'POST',
          data: grnForm,
        });
        const count = await offlineDB.pendingCount();
        if (window.__setOfflinePendingCount) window.__setOfflinePendingCount(count);
        toast.success('Saved locally — will sync when back online', {
          description: 'GRN stored on this device',
          duration: 4000,
        });
        setGrnDialogOpen(false);
        setGrnForm({ quantity_received: '', invoice_ref: '', delivery_note: '', received_date: new Date().toISOString().split('T')[0], notes: '', part_number_confirmed: '' });
      } catch (_e) {
        toast.error('Failed to save offline. Storage may be full.');
      }
      return;
    }

    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(
          `${API}/purchase-requisitions/${selectedPR.id}/grn`,
          grnForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(res.data.message);
        setGrnDialogOpen(false);
        setGrnForm({ quantity_received: '', invoice_ref: '', delivery_note: '', received_date: new Date().toISOString().split('T')[0], notes: '', part_number_confirmed: '' });
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to record GRN');
      }
    });
  };

  const openGrnHistory = async (pr) => {
    setSelectedPR(pr);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/purchase-requisitions/${pr.id}/grn`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGrnHistory(res.data);
      setGrnHistoryOpen(true);
    } catch (_e) {
      toast.error('Failed to fetch GRN history');
    }
  };

  const openIssueDialog = (prId) => {
    setIssuePrId(prId);
    setIssueToName('');
    setIssueDialogOpen(true);
  };

  const handleIssueToMaintenance = async () => {
    if (!issuePrId) return;
    guard(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API}/purchase-requisitions/${issuePrId}/issue-to-maintenance`,
          { issued_to_name: issueToName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Issued to maintenance');
        setIssueDialogOpen(false);
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to issue');
      }
    });
  };

  const openOrderDialog = (pr) => {
    setSelectedPR(pr);
    setOrderForm({
      actual_unit_price: pr.estimated_unit_price || 0,
      vendor_name: pr.vendor_name || '',
      quantity_ordered: pr.quantity_requested
    });
    setOrderDialogOpen(true);
  };

  const openGrnDialog = (pr) => {
    setSelectedPR(pr);
    const remaining = (pr.quantity_ordered || pr.quantity_requested) - (pr.quantity_received || 0);
    setGrnForm({ quantity_received: Math.max(remaining, 1), invoice_ref: '', delivery_note: '', received_date: new Date().toISOString().split('T')[0], notes: '', part_number_confirmed: pr.part_number || '' });
    setGrnDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{VIEW_CONFIG[activeView].label}</h1>
          <p className="text-slate-600 mt-1">
            {activeView === 'indent' && 'New indents raised — awaiting approval'}
            {activeView === 'quotations' && 'Approved indents — compare vendor quotes before raising PO'}
            {activeView === 'pending' && 'Orders placed with vendors — awaiting full delivery'}
            {activeView === 'completed' && `Completed in ${new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}`}
            {activeView === 'history' && 'All received, issued, and cancelled orders — full history'}
          </p>
        </div>
        <div className="flex gap-2">
          <ExportButton
            rows={filteredRequisitions}
            columns={[
              { header: 'REQ #', key: 'pr_number' },
              { header: 'PO #', key: 'po_number' },
              { header: 'Part Name', key: 'part_name' },
              { header: 'Part Number', key: 'part_number' },
              { header: 'Asset', key: 'door_no' },
              { header: 'Vendor', key: 'vendor_name' },
              { header: 'Qty Req.', key: 'quantity_requested' },
              { header: 'Qty Ordered', key: 'quantity_ordered' },
              { header: 'Qty Received', key: 'quantity_received' },
              { header: 'Est. Unit Price', key: 'estimated_unit_price' },
              { header: 'Actual Unit Price', key: 'actual_unit_price' },
              { header: 'Status', key: 'status' },
              { header: 'Urgency', key: 'urgency' },
              { header: 'Created By', key: 'created_by' },
              { header: 'Notes', key: 'notes' },
            ]}
            filename={`purchase_orders_${new Date().toISOString().slice(0,10)}`}
            title="Purchase Orders Report"
            subtitle={`Exported on ${new Date().toLocaleDateString('en-IN')}`}
          />
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="h-4 w-4 mr-2" />
            Add Quote
          </Button>
        </div>
      </div>

      {/* Stats Cards — view-based counts */}
      <PendingDeletionsPanel module="purchase_orders" requests={pendingDelRequests} onApprove={handleApproveDelete} onReject={handleRejectDelete} />
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { view: 'indent',     label: 'Indent',           icon: Clock,        color: 'bg-yellow-100 text-yellow-800' },
          { view: 'quotations', label: 'Quotations',       icon: Package,      color: 'bg-purple-100 text-purple-800' },
          { view: 'pending',    label: 'Pending Delivery', icon: Truck,        color: 'bg-blue-100 text-blue-800' },
          { view: 'completed',  label: 'Completed (Month)',icon: CheckCircle,  color: 'bg-green-100 text-green-800' },
          { view: 'history',    label: 'All History',      icon: History,      color: 'bg-slate-100 text-slate-700' },
        ].map(({ view, label, icon: Icon, color }) => {
          const base = requisitions.filter(pr => classifyPR(pr) === (view === 'history' ? 'completed' : view));
          let count = base.length;
          if (view === 'completed') {
            const now = new Date();
            const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            count = base.filter(pr => (pr.updated_at || pr.created_at || '').startsWith(monthStr)).length;
          }
          return (
            <Card
              key={view}
              className={`cursor-pointer transition-all hover:shadow-md ${activeView === view ? 'ring-2 ring-slate-900' : ''}`}
              onClick={() => navigate(view === 'quotations' ? '/inventory/quotations/received' : `/inventory/purchase-orders/${view}`)}
              data-testid={`stat-card-${view}`}
            >
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-600">{label}</p>
                    <p className="text-2xl font-bold">{count}</p>
                  </div>
                  <div className={`p-2 rounded-full ${color}`}><Icon className="h-5 w-5" /></div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Price Alerts Banner */}
      {stats?.price_alerts > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <span className="text-amber-800">
            {stats.price_alerts} purchase order(s) have abnormally high prices - please review
          </span>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search by part name, part number, indent #, PO # or asset..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="pl-9 max-w-md"
          data-testid="purchase-search-input"
        />
      </div>

      {/* Requisitions Table */}
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Indent #</TableHead>
              <TableHead>Part</TableHead>
              <TableHead>Asset</TableHead>
              <TableHead className="text-center">Qty</TableHead>
              <TableHead className="text-right">Est. Price</TableHead>
              <TableHead className="text-right">Actual Price</TableHead>
              <TableHead>Urgency</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>PO #</TableHead>
              <TableHead>Issued To</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRequisitions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  {VIEW_CONFIG[activeView].emptyMsg}
                </TableCell>
              </TableRow>
            ) : (
              filteredRequisitions.map((pr) => {
                const statusConfig = STATUS_CONFIG[pr.status] || STATUS_CONFIG.pending;
                const urgencyConfig = URGENCY_CONFIG[pr.urgency] || URGENCY_CONFIG.normal;
                return (
                  <TableRow key={pr.id} className={pr.price_alert ? 'bg-amber-50' : ''}>
                    <TableCell className="font-mono text-sm">
                      <button
                        className="text-left text-blue-700 hover:text-blue-900 hover:underline font-mono font-medium"
                        onClick={() => { setDetailPR(pr); setDetailOpen(true); }}
                        data-testid={`indent-num-${pr.id}`}
                      >
                        {pr.pr_number}
                      </button>
                      {pr.price_alert && (
                        <AlertTriangle className="h-4 w-4 text-amber-500 inline ml-1" title={pr.price_alert_message} />
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{pr.part_name}</p>
                        {pr.part_number && (
                          <p className="text-xs text-slate-500">{pr.part_number}</p>
                        )}
                        <p className="text-xs text-slate-400">Source: {pr.source}</p>
                      </div>
                    </TableCell>
                    <TableCell>{pr.door_no || '-'}</TableCell>
                    <TableCell className="text-center">
                      <div>
                        <span>{pr.quantity_requested}</span>
                        {pr.quantity_received > 0 && (
                          <span className="text-xs text-green-600 block">
                            ({pr.quantity_received} rcvd)
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {pr.estimated_unit_price > 0 ? `₹${pr.estimated_unit_price.toLocaleString()}` : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {pr.actual_unit_price > 0 ? `₹${pr.actual_unit_price.toLocaleString()}` : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={urgencyConfig.color}>{urgencyConfig.label}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusConfig.color}>
                        {statusConfig.label}
                        {pr.auto_approved && pr.status === 'approved' && (
                          <span className="ml-1">(Auto)</span>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {pr.po_number ? (
                        <button
                          className="text-left text-indigo-700 hover:text-indigo-900 hover:underline font-mono font-medium"
                          onClick={() => { setDetailPR(pr); setDetailOpen(true); }}
                          data-testid={`po-num-${pr.id}`}
                        >
                          {pr.po_number}
                        </button>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {pr.status === 'issued' && pr.issued_to_name ? (
                        <span className="text-emerald-700 font-medium">{pr.issued_to_name}</span>
                      ) : pr.status === 'issued' ? (
                        <span className="text-slate-400 text-xs">—</span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {pr.status === 'pending' && (user?.is_admin || user?.approval_rights?.can_approve_po) && (
                          <Button size="sm" onClick={() => handleApprove(pr.id)}>
                            Approve
                          </Button>
                        )}
                        {pr.status === 'approved' && (
                          <Button size="sm" onClick={() => openOrderDialog(pr)} className="bg-indigo-600 hover:bg-indigo-700">
                            Mark Ordered
                          </Button>
                        )}
                        {(pr.status === 'ordered' || pr.status === 'partial') && (
                          <>
                            <Button size="sm" onClick={() => openGrnDialog(pr)} className="bg-green-600 hover:bg-green-700">
                              Record Inward
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openGrnHistory(pr)} className="text-xs">
                              GRN History
                            </Button>
                          </>
                        )}
                        {pr.status === 'received' && (
                          <Button size="sm" variant="outline" onClick={() => openGrnHistory(pr)} className="text-xs">
                            GRN History
                          </Button>
                        )}
                        {pr.status === 'received' && pr.door_no && (
                          <Button size="sm" onClick={() => openIssueDialog(pr.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`issue-btn-${pr.id}`}>
                            Issue
                          </Button>
                        )}
                        {/* Download PO PDF */}
                        {pr.po_number && (
                          <Button size="sm" variant="outline" onClick={() => handleDownloadPO(pr)} title="Download PO PDF" data-testid={`download-po-${pr.id}`}>
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {/* Edit PO */}
                        {!['received', 'issued'].includes(pr.status) && (
                          <Button size="sm" variant="outline" onClick={() => openEditPO(pr)} title="Edit PO" data-testid={`edit-po-${pr.id}`}>
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {/* Vendor Quotes */}
                        <Button size="sm" variant="outline" onClick={() => openVendorQuotes(pr)} title="Vendor Quotes" data-testid={`quotes-${pr.id}`}>
                          <Quote className="h-3.5 w-3.5" />
                        </Button>
                        {!['received', 'issued'].includes(pr.status) && (
                          <Button size="sm" variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeletePR(pr)}
                            data-testid={`delete-pr-${pr.id}`}
                            title="Delete this PO">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create PR Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Quote / Purchase Requisition</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePR} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Part Name *</Label>
                <Input
                  value={newPR.part_name}
                  onChange={(e) => setNewPR({ ...newPR, part_name: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Part Number</Label>
                <Input
                  value={newPR.part_number}
                  onChange={(e) => setNewPR({ ...newPR, part_number: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  min="1"
                  value={newPR.quantity_requested}
                  onChange={(e) => setNewPR({ ...newPR, quantity_requested: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Estimated Unit Price (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  value={newPR.estimated_unit_price}
                  onChange={(e) => setNewPR({ ...newPR, estimated_unit_price: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Vendor Name</Label>
                <Input
                  value={newPR.vendor_name}
                  onChange={(e) => setNewPR({ ...newPR, vendor_name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Purchase Type *</Label>
                <Select value={newPR.purchase_type} onValueChange={(val) => setNewPR({ ...newPR, purchase_type: val })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ho">HO Purchase (Head Office)</SelectItem>
                    <SelectItem value="local">Local Purchase</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Consignee Address (Delivery Site)</Label>
                <Select value={newPR.consignee_address} onValueChange={(val) => setNewPR({ ...newPR, consignee_address: val })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select delivery site..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(settings?.site_addresses || []).map(addr => (
                      <SelectItem key={addr} value={addr}>{addr}</SelectItem>
                    ))}
                    <SelectItem value="custom">Enter manually...</SelectItem>
                  </SelectContent>
                </Select>
                {newPR.consignee_address === 'custom' && (
                  <Input
                    className="mt-1.5"
                    placeholder="Enter delivery address..."
                    onChange={(e) => setNewPR({ ...newPR, consignee_address: e.target.value })}
                  />
                )}
              </div>
              <div className="col-span-2">
                <Label>Urgency</Label>
                <Select value={newPR.urgency} onValueChange={(val) => setNewPR({ ...newPR, urgency: val })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={newPR.notes}
                  onChange={(e) => setNewPR({ ...newPR, notes: e.target.value })}
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Add Quote</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit PO Dialog */}
      <Dialog open={editPoOpen} onOpenChange={setEditPoOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit PO — {selectedPR?.po_number || selectedPR?.pr_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Part Name</Label>
                <Input className="mt-1" value={editPoForm.part_name || ''} onChange={e => setEditPoForm(f => ({ ...f, part_name: e.target.value }))} />
              </div>
              <div>
                <Label>Part Number</Label>
                <Input className="mt-1" value={editPoForm.part_number || ''} onChange={e => setEditPoForm(f => ({ ...f, part_number: e.target.value }))} />
              </div>
              <div>
                <Label>Qty Requested</Label>
                <Input type="number" min="1" className="mt-1" value={editPoForm.quantity_requested} onChange={e => setEditPoForm(f => ({ ...f, quantity_requested: e.target.value }))} />
              </div>
              <div>
                <Label>Vendor Name</Label>
                <Input className="mt-1" value={editPoForm.vendor_name || ''} onChange={e => setEditPoForm(f => ({ ...f, vendor_name: e.target.value }))} />
              </div>
              <div>
                <Label>Unit Price (₹)</Label>
                <Input type="number" min="0" className="mt-1" value={editPoForm.actual_unit_price} onChange={e => setEditPoForm(f => ({ ...f, actual_unit_price: e.target.value }))} />
              </div>
              <div>
                <Label>GST %</Label>
                <Input type="number" min="0" max="100" className="mt-1" value={editPoForm.gst_percentage} onChange={e => setEditPoForm(f => ({ ...f, gst_percentage: e.target.value }))} />
              </div>
              <div>
                <Label>Discount (₹)</Label>
                <Input type="number" min="0" className="mt-1" value={editPoForm.discount} onChange={e => setEditPoForm(f => ({ ...f, discount: e.target.value }))} />
              </div>
              <div>
                <Label>Purchase Type</Label>
                <Select value={editPoForm.purchase_type || 'ho'} onValueChange={val => setEditPoForm(f => ({ ...f, purchase_type: val }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ho">HO Purchase</SelectItem>
                    <SelectItem value="local">Local Purchase</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Consignee Address</Label>
                <Select value={editPoForm.consignee_address || ''} onValueChange={val => setEditPoForm(f => ({ ...f, consignee_address: val }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {(settings?.site_addresses || []).map(addr => <SelectItem key={addr} value={addr}>{addr}</SelectItem>)}
                    <SelectItem value="__custom__">Enter manually...</SelectItem>
                  </SelectContent>
                </Select>
                {editPoForm.consignee_address === '__custom__' && (
                  <Input className="mt-1.5" placeholder="Enter address..." onChange={e => setEditPoForm(f => ({ ...f, consignee_address: e.target.value }))} />
                )}
              </div>
              <div>
                <Label>Payment Terms</Label>
                <Input className="mt-1" value={editPoForm.payment_terms || ''} onChange={e => setEditPoForm(f => ({ ...f, payment_terms: e.target.value }))} placeholder="e.g. 15 Days Credit" />
              </div>
              <div>
                <Label>Delivery</Label>
                <Input className="mt-1" value={editPoForm.delivery || ''} onChange={e => setEditPoForm(f => ({ ...f, delivery: e.target.value }))} placeholder="e.g. At site" />
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea className="mt-1" rows={2} value={editPoForm.notes || ''} onChange={e => setEditPoForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditPoOpen(false)}>Cancel</Button>
              <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleEditPO}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Vendor Quotes Dialog */}
      <Dialog open={quotesOpen} onOpenChange={setQuotesOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vendor Quotes — {selectedPR?.part_name}</DialogTitle>
          </DialogHeader>
          {/* Existing quotes */}
          {quotesData.length > 0 && (
            <div className="mb-4 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Vendor</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead className="text-right">GST %</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const minPrice = Math.min(...quotesData.map(q => q.unit_price));
                    return quotesData.map(q => {
                      const isBest = q.unit_price === minPrice;
                      return (
                        <TableRow key={q.id} className={isBest ? 'bg-green-50' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div>
                                <p className="font-medium text-sm">{q.vendor_name}</p>
                                {q.vendor_contact && <p className="text-xs text-slate-400">{q.vendor_contact}</p>}
                              </div>
                              {isBest && (
                                <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-green-100 border border-green-300 px-2 py-0.5 text-xs font-semibold text-green-700" data-testid={`best-quote-${q.id}`}>
                                  Best Price
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className={`text-right font-semibold ${isBest ? 'text-green-700' : ''}`}>₹{q.unit_price.toLocaleString()}</TableCell>
                          <TableCell className="text-right">{q.gst_percentage}%</TableCell>
                          <TableCell className="text-right font-semibold">₹{q.total_price.toLocaleString()}</TableCell>
                          <TableCell className="text-sm">{q.quote_date}</TableCell>
                          <TableCell className="text-sm text-slate-500">{q.notes}</TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteQuote(q.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                </TableBody>
              </Table>
            </div>
          )}
          {quotesData.length === 0 && (
            <p className="text-sm text-slate-400 text-center py-4">No quotes recorded yet.</p>
          )}
          {/* Add new quote form */}
          <div className="border-t pt-4">
            <p className="text-sm font-semibold text-slate-700 mb-3">Add New Quote</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Vendor Name *</Label>
                <Input className="mt-1 h-8" value={newQuote.vendor_name} onChange={e => setNewQuote(q => ({ ...q, vendor_name: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Contact</Label>
                <Input className="mt-1 h-8" value={newQuote.vendor_contact} onChange={e => setNewQuote(q => ({ ...q, vendor_contact: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input className="mt-1 h-8" type="email" value={newQuote.vendor_email} onChange={e => setNewQuote(q => ({ ...q, vendor_email: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Unit Price (₹) *</Label>
                <Input className="mt-1 h-8" type="number" min="0" value={newQuote.unit_price} onChange={e => setNewQuote(q => ({ ...q, unit_price: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">GST %</Label>
                <Input className="mt-1 h-8" type="number" min="0" value={newQuote.gst_percentage} onChange={e => setNewQuote(q => ({ ...q, gst_percentage: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Quote Date</Label>
                <Input className="mt-1 h-8" type="date" value={newQuote.quote_date} onChange={e => setNewQuote(q => ({ ...q, quote_date: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Qty</Label>
                <Input className="mt-1 h-8" type="number" min="1" value={newQuote.quantity} onChange={e => setNewQuote(q => ({ ...q, quantity: e.target.value }))} />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Notes</Label>
                <Input className="mt-1 h-8" value={newQuote.notes} onChange={e => setNewQuote(q => ({ ...q, notes: e.target.value }))} placeholder="Valid until, special conditions..." />
              </div>
            </div>
            <div className="flex justify-between items-center gap-2 mt-3">
              {(() => {
                const matchKey = (selectedPR?.part_name || '').toLowerCase();
                const matchCount = requisitions.filter(pr => pr.id !== selectedPR?.id && (pr.part_name || '').toLowerCase() === matchKey).length;
                if (matchCount > 0) return (
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="apply-to-all-checkbox">
                    <input type="checkbox" checked={applyToAll} onChange={e => setApplyToAll(e.target.checked)} className="rounded" />
                    Apply to all {matchCount + 1} indents for "{selectedPR?.part_name}"
                  </label>
                );
                return <div />;
              })()}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setQuotesOpen(false)}>Close</Button>
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={handleAddQuote} data-testid="add-quote-btn">
                  Save Quote
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Dialog */}
      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Purchase Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-600">REQ: {selectedPR?.pr_number}</p>
              <p className="font-medium">{selectedPR?.part_name}</p>
              <p className="text-sm text-slate-600">Qty: {selectedPR?.quantity_requested}</p>
              <p className="text-xs text-blue-600 mt-1">A PO number (LSN/HO/SFT/...) will be auto-generated on save</p>
            </div>
            <div>
              <Label>Vendor Name *</Label>
              <Input
                value={orderForm.vendor_name}
                onChange={(e) => setOrderForm({ ...orderForm, vendor_name: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Actual Unit Price (₹) *</Label>
                <Input
                  type="number"
                  min="0"
                  value={orderForm.actual_unit_price}
                  onChange={(e) => setOrderForm({ ...orderForm, actual_unit_price: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Quantity Ordered</Label>
                <Input
                  type="number"
                  min="1"
                  value={orderForm.quantity_ordered}
                  onChange={(e) => setOrderForm({ ...orderForm, quantity_ordered: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <p className="text-sm text-slate-500">
              Total: ₹{((parseFloat(orderForm.actual_unit_price) || 0) * (parseFloat(orderForm.quantity_ordered) || selectedPR?.quantity_requested || 0)).toLocaleString()}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOrderDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleMarkOrdered} className="bg-indigo-600 hover:bg-indigo-700">Record Order</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* GRN — Material Inward Dialog */}
      <Dialog open={grnDialogOpen} onOpenChange={setGrnDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Record Material Inward (GRN)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 p-4 rounded-lg text-sm">
              <p className="font-medium">{selectedPR?.part_name}</p>
              {selectedPR?.part_number && (
                <p className="text-xs font-mono text-indigo-700 mt-0.5">Part No: {selectedPR.part_number}</p>
              )}
              <p className="text-slate-500">REQ: {selectedPR?.pr_number} | PO: {selectedPR?.po_number || 'pending'}</p>
              <div className="flex gap-4 mt-2">
                <span>Ordered: <strong>{selectedPR?.quantity_ordered || selectedPR?.quantity_requested}</strong></span>
                <span>Already Received: <strong className="text-green-600">{selectedPR?.quantity_received || 0}</strong></span>
                <span>Pending: <strong className="text-orange-600">
                  {(selectedPR?.quantity_ordered || selectedPR?.quantity_requested || 0) - (selectedPR?.quantity_received || 0)}
                </strong></span>
              </div>
            </div>
            {/* Part Number Confirmation */}
            <div>
              <Label>Part Number (Confirm on Physical Item)</Label>
              <Input
                value={grnForm.part_number_confirmed}
                onChange={(e) => setGrnForm({ ...grnForm, part_number_confirmed: e.target.value })}
                placeholder={selectedPR?.part_number || 'e.g. PN-2024-001'}
                className="mt-1 font-mono"
                data-testid="grn-part-number-input"
              />
              <p className="text-xs text-slate-400 mt-0.5">Verify part number from physical label or supplier invoice</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Qty Received Now *</Label>
                <Input
                  type="number"
                  min="1"
                  value={grnForm.quantity_received}
                  onChange={(e) => setGrnForm({ ...grnForm, quantity_received: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Received Date</Label>
                <Input
                  type="date"
                  value={grnForm.received_date}
                  onChange={(e) => setGrnForm({ ...grnForm, received_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Invoice / Bill Ref</Label>
                <Input
                  value={grnForm.invoice_ref}
                  onChange={(e) => setGrnForm({ ...grnForm, invoice_ref: e.target.value })}
                  placeholder="e.g. INV-2026-001"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Delivery Note / DC</Label>
                <Input
                  value={grnForm.delivery_note}
                  onChange={(e) => setGrnForm({ ...grnForm, delivery_note: e.target.value })}
                  placeholder="Delivery Challan no."
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={grnForm.notes}
                onChange={(e) => setGrnForm({ ...grnForm, notes: e.target.value })}
                placeholder="Any remarks"
                className="mt-1"
              />
            </div>
            <p className="text-xs text-slate-500">Inventory will be updated by {grnForm.quantity_received} units on save</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setGrnDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddGRN} className="bg-green-600 hover:bg-green-700" data-testid="grn-submit-btn">Save GRN</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* GRN History Dialog */}
      <Dialog open={grnHistoryOpen} onOpenChange={setGrnHistoryOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>GRN History — {selectedPR?.part_name}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-500 mb-3">
            PO: {selectedPR?.po_number || '—'} | Ordered: {selectedPR?.quantity_ordered || selectedPR?.quantity_requested} | Total Received: {selectedPR?.quantity_received || 0}
          </div>
          {grnHistory.length === 0 ? (
            <p className="text-center py-8 text-slate-400">No inward entries yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Part No.</TableHead>
                  <TableHead className="text-center">Qty</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>DC</TableHead>
                  <TableHead>Received By</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {grnHistory.map(g => (
                  <TableRow key={g.id}>
                    <TableCell className="text-sm">{g.received_date}</TableCell>
                    <TableCell className="text-sm font-mono text-indigo-700">{g.part_number_confirmed || '—'}</TableCell>
                    <TableCell className="text-center font-semibold">{g.quantity_received}</TableCell>
                    <TableCell className="text-sm">{g.invoice_ref || '—'}</TableCell>
                    <TableCell className="text-sm">{g.delivery_note || '—'}</TableCell>
                    <TableCell className="text-sm">{g.received_by}</TableCell>
                    <TableCell className="text-sm">{g.notes || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Issue To Maintenance Dialog */}
      <Dialog open={issueDialogOpen} onOpenChange={(open) => { if (!open) { setIssueDialogOpen(false); setIssueToName(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Issue Parts to Maintenance</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Enter the name of the person receiving these parts from the store.
            </p>
            <div>
              <Label>Issued To *</Label>
              <Input
                value={issueToName}
                onChange={(e) => setIssueToName(e.target.value)}
                placeholder="e.g. Ramu (Mechanic), Driver - D3"
                className="mt-1"
                autoFocus
                data-testid="issued-to-name-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setIssueDialogOpen(false); setIssueToName(''); }}>
                Cancel
              </Button>
              <Button
                onClick={handleIssueToMaintenance}
                disabled={!issueToName.trim()}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-issue-btn"
              >
                Confirm Issue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteRequestDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        module="purchase_orders"
        entryId={deleteTarget?.id}
        entryDisplay={deleteTarget?.display}
        onSuccess={() => setDeleteTarget(null)}
      />

      {/* Detail / Ticket View Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-600" />
              {detailPR?.po_number ? `Purchase Order — ${detailPR.po_number}` : `Indent — ${detailPR?.pr_number}`}
            </DialogTitle>
          </DialogHeader>
          {detailPR && (
            <div className="space-y-4 text-sm">
              {/* Ref Numbers */}
              <div className="grid grid-cols-2 gap-3 bg-slate-50 rounded-lg p-4">
                <div>
                  <p className="text-xs text-slate-500">Indent #</p>
                  <p className="font-mono font-semibold text-slate-900">{detailPR.pr_number || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">PO #</p>
                  <p className="font-mono font-semibold text-indigo-700">{detailPR.po_number || 'Not yet raised'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Status</p>
                  <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_CONFIG[detailPR.status]?.color || 'bg-slate-100'}`}>
                    {STATUS_CONFIG[detailPR.status]?.label || detailPR.status}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Urgency</p>
                  <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-xs font-semibold ${URGENCY_CONFIG[detailPR.urgency]?.color || ''}`}>
                    {URGENCY_CONFIG[detailPR.urgency]?.label || detailPR.urgency}
                  </span>
                </div>
              </div>

              {/* Part Info */}
              <div>
                <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-2">Part Details</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <p className="text-xs text-slate-400">Part Name</p>
                    <p className="font-medium text-slate-900">{detailPR.part_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Part Number</p>
                    <p className="font-mono">{detailPR.part_number || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Asset / Door No.</p>
                    <p>{detailPR.door_no || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Source</p>
                    <p className="capitalize">{detailPR.source || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Purchase Type</p>
                    <p className="capitalize">{detailPR.purchase_type === 'ho' ? 'HO Purchase' : detailPR.purchase_type === 'local' ? 'Local Purchase' : '—'}</p>
                  </div>
                </div>
              </div>

              {/* Quantities & Pricing */}
              <div>
                <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-2">Quantities & Pricing</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-xs text-slate-400">Qty Requested</p>
                    <p className="font-semibold">{detailPR.quantity_requested}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Qty Ordered</p>
                    <p className="font-semibold">{detailPR.quantity_ordered || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Qty Received</p>
                    <p className="font-semibold text-green-700">{detailPR.quantity_received || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Est. Unit Price</p>
                    <p>{detailPR.estimated_unit_price > 0 ? `₹${detailPR.estimated_unit_price.toLocaleString()}` : '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Actual Unit Price</p>
                    <p className="font-semibold">{detailPR.actual_unit_price > 0 ? `₹${detailPR.actual_unit_price.toLocaleString()}` : '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">GST %</p>
                    <p>{detailPR.gst_percentage ?? '—'}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Discount</p>
                    <p>{detailPR.discount > 0 ? `₹${detailPR.discount.toLocaleString()}` : '—'}</p>
                  </div>
                </div>
              </div>

              {/* Vendor & Delivery */}
              <div>
                <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-2">Vendor & Delivery</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <p className="text-xs text-slate-400">Vendor</p>
                    <p className="font-medium">{detailPR.vendor_name || '—'}</p>
                    {detailPR.vendor_contact && <p className="text-slate-500 text-xs">{detailPR.vendor_contact}</p>}
                    {detailPR.vendor_email && <p className="text-slate-500 text-xs">{detailPR.vendor_email}</p>}
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-slate-400">Consignee / Delivery Address</p>
                    <p>{detailPR.consignee_address || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Payment Terms</p>
                    <p>{detailPR.payment_terms || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Delivery</p>
                    <p>{detailPR.delivery || '—'}</p>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div>
                <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-2">Timeline</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-400">Raised By</p>
                    <p>{detailPR.created_by || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Created At</p>
                    <p>{detailPR.created_at ? new Date(detailPR.created_at).toLocaleString('en-IN') : '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Approved By</p>
                    <p>{detailPR.approved_by || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Ordered At</p>
                    <p>{detailPR.ordered_at ? new Date(detailPR.ordered_at).toLocaleString('en-IN') : '—'}</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {detailPR.notes && (
                <div>
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide mb-1">Notes</p>
                  <p className="bg-slate-50 rounded-lg p-3 text-slate-700">{detailPR.notes}</p>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrdersPage;
